import { SentenceTranslationData } from '../../../dto/news-content';

const MIN_WORDS_PER_SECTION = 5;
const MAX_WORDS_PER_SECTION = 7;

export class TextSplitter {
	splitBySentenceWordCount(
		content: readonly SentenceTranslationData[],
		titleWordCount: number = 0
	): SentenceTranslationData[][] {
		const sentences = Array.isArray(content) ? [...content] : [];
		if (sentences.length === 0) return [];

		const wordCounts = sentences.map((s) => this.countTranslatedWords(s));
		const totalWords = wordCounts.reduce((a, b) => a + b, 0) + titleWordCount;

		if (totalWords <= MAX_WORDS_PER_SECTION) {
			return [sentences];
		}

		return this.greedySplit(sentences, wordCounts, titleWordCount);
	}

	countTranslatedWords(sentence: SentenceTranslationData): number {
		let count = 0;
		for (const word of sentence.words) {
			if (word.translation && word.normalizedWordId) {
				count++;
			}
		}
		return count;
	}

	/**
	 * @deprecated Use splitBySentenceWordCount instead
	 */
	splitIntoThreeParts(content: readonly SentenceTranslationData[]): [
		SentenceTranslationData[],
		SentenceTranslationData[],
		SentenceTranslationData[]
	] {
		const source = Array.isArray(content) ? [...content] : [];
		const total = source.length;

		if (total === 0) {
			return [[], [], []];
		}

		const base = Math.floor(total / 3);
		const remainder = total % 3;
		const firstSize = base + (remainder > 0 ? 1 : 0);
		const secondSize = base + (remainder > 1 ? 1 : 0);
		const thirdSize = total - firstSize - secondSize;

		const first = source.slice(0, firstSize);
		const second = source.slice(firstSize, firstSize + secondSize);
		const third = source.slice(firstSize + secondSize, firstSize + secondSize + thirdSize);

		return [first, second, third];
	}

	private greedySplit(
		sentences: SentenceTranslationData[],
		wordCounts: number[],
		titleWordCount: number
	): SentenceTranslationData[][] {
		const sections: SentenceTranslationData[][] = [];
		let currentSection: SentenceTranslationData[] = [];
		let currentWordCount = titleWordCount;

		for (let i = 0; i < sentences.length; i++) {
			const sentenceWords = wordCounts[i];
			const countAfterAdd = currentWordCount + sentenceWords;
			const remainingWords = this.sumFrom(wordCounts, i);
			const totalRemaining = currentWordCount + remainingWords;

			if (currentSection.length === 0) {
				currentSection.push(sentences[i]);
				currentWordCount = countAfterAdd;
				continue;
			}

			if (countAfterAdd <= MAX_WORDS_PER_SECTION) {
				currentSection.push(sentences[i]);
				currentWordCount = countAfterAdd;
				continue;
			}

			// Adding this sentence would exceed MAX. Decide whether to split here.
			if (currentWordCount >= MIN_WORDS_PER_SECTION) {
				const wordsLeft = totalRemaining - currentWordCount;
				if (wordsLeft >= MIN_WORDS_PER_SECTION) {
					sections.push(currentSection);
					currentSection = [sentences[i]];
					currentWordCount = sentenceWords;
					continue;
				}
			}

			// Can't split cleanly — absorb the sentence into the current section
			currentSection.push(sentences[i]);
			currentWordCount = countAfterAdd;

			if (currentWordCount >= MAX_WORDS_PER_SECTION) {
				const wordsAfter = this.sumFrom(wordCounts, i + 1);
				if (wordsAfter >= MIN_WORDS_PER_SECTION) {
					sections.push(currentSection);
					currentSection = [];
					currentWordCount = 0;
				}
			}
		}

		if (currentSection.length > 0) {
			if (sections.length > 0 && currentWordCount < MIN_WORDS_PER_SECTION) {
				const lastSection = sections[sections.length - 1];
				sections[sections.length - 1] = [...lastSection, ...currentSection];
			} else {
				sections.push(currentSection);
			}
		}

		return sections;
	}

	private sumFrom(counts: number[], startIndex: number): number {
		let sum = 0;
		for (let i = startIndex; i < counts.length; i++) {
			sum += counts[i];
		}
		return sum;
	}
}
